import { AnimatePresence } from 'framer-motion';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';

import Overlay from 'components/Overlay/Overlay';
import {
  FaqType,
  JoinUsPageType,
  OpportunitiesData,
} from 'containers/JoinUsPage/JoinUsPage.types';
import FaqOverlay from 'containers/JoinUsPage/sections/FaqOverlay/FaqOverlay';
import JoinUsOpportunityView from 'containers/JoinUsPage/sections/JoinUsOpportunityView/JoinUsOpportunityView';
import OpportunityListOverlay from 'containers/JoinUsPage/sections/OpportunityListOverlay/OpportunityListOverlay';
import { useOpportunitiesStore } from 'store';

import * as S from './OpportunityOverlayWrapper.styles';

export interface OpportunityOverlayWrapperProps {
  faqData: Array<FaqType>;
  pageData: JoinUsPageType;
  opportunitiesData: OpportunitiesData;
}

const defaultProps: Partial<OpportunityOverlayWrapperProps> = {};

const OpportunityOverlayWrapper: React.FunctionComponent<
  OpportunityOverlayWrapperProps
> = ({ faqData, pageData, opportunitiesData }) => {
  const {
    isFaqOpen,
    isOpportunityListOpen,
    isOpportunityOpen,
    activeOpportunity,
    openOpportunity,
    closeOverlay,
    backToOpportunities,
  } = useOpportunitiesStore();
  const { query, push, pathname } = useRouter();
  const overlayRef = useRef<HTMLDivElement>(null);
  const [delay, setDelay] = useState(1);

  useEffect(() => {
    document.documentElement.style.overflow =
      isFaqOpen || isOpportunityListOpen || isOpportunityOpen
        ? 'hidden'
        : 'auto';
    overlayRef.current?.scrollTo(0, 0);

    return () => {
      document.documentElement.style.overflow = 'auto';
    };
  }, [isFaqOpen, isOpportunityListOpen, isOpportunityOpen]);

  useEffect(() => {
    if (query.job) {
      const urlOpportunity = opportunitiesData.opportunities.find(
        opportunity => `${opportunity.internal_job_id}` === query.job
      );
      if (urlOpportunity) {
        openOpportunity(urlOpportunity);
      }
    }
  }, [query, opportunitiesData]);

  const backToOpportunityList = () => {
    backToOpportunities();
    push(`${pathname}`, undefined, { shallow: true });
  };

  return (
    <Overlay
      ref={overlayRef}
      isOpen={isFaqOpen || isOpportunityListOpen || isOpportunityOpen}
      CloseButton={isOpportunityOpen ? S.BackIconComp : S.CloseIconComp}
      delay={delay}
      onExitClick={() => {
        if (isOpportunityListOpen || isFaqOpen) {
          setDelay(1);
          closeOverlay();
        } else {
          setDelay(0);
          backToOpportunityList();
        }
      }}
    >
      <AnimatePresence initial={false} mode="wait">
        {isOpportunityListOpen && (
          <OpportunityListOverlay
            key="opportunityListOverlay"
            data={opportunitiesData}
            pageData={pageData}
          />
        )}
        {isOpportunityOpen && activeOpportunity && (
          <JoinUsOpportunityView key="opportunityOverlay" pageData={pageData} />
        )}
        {isFaqOpen && (
          <FaqOverlay key="faqOverlay" data={faqData} pageData={pageData} />
        )}
      </AnimatePresence>
    </Overlay>
  );
};

OpportunityOverlayWrapper.defaultProps = defaultProps;

export default OpportunityOverlayWrapper;
